import { jsPDF } from "jspdf";
import { blobToDataURL, canvasToBlob, clamp } from "./utils.js";

export function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("图片加载失败"));
    img.src = src;
  });
}

export function imageFilter(edits = {}) {
  const brightness = edits.brightness == null ? 100 : edits.brightness;
  const contrast = edits.contrast == null ? 100 : edits.contrast;
  const saturate = edits.saturate == null ? 100 : edits.saturate;
  return `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturate}%)`;
}

export async function renderEditedCanvas(img, edits = {}) {
  const rotation = ((Number(edits.rotation) || 0) % 360 + 360) % 360;
  const swap = rotation === 90 || rotation === 270;
  const width = swap ? img.naturalHeight : img.naturalWidth;
  const height = swap ? img.naturalWidth : img.naturalHeight;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (edits.brightness || edits.contrast || edits.saturate) {
    ctx.filter = imageFilter(edits);
  }
  ctx.translate(width / 2, height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(edits.flipH ? -1 : 1, edits.flipV ? -1 : 1);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return canvas;
}

export async function convertImage(blob, { format = "png", quality = 0.92, edits = {} } = {}) {
  const url = await blobToDataURL(blob);
  const img = await loadImageElement(url);
  const canvas = await renderEditedCanvas(img, edits);
  const type = format === "jpg" ? "image/jpeg" : format === "webp" ? "image/webp" : "image/png";
  return canvasToBlob(canvas, type, format === "png" ? undefined : quality);
}

export async function compressImage(blob, { maxWidth = 1600, quality = 0.8 } = {}) {
  const url = await blobToDataURL(blob);
  const img = await loadImageElement(url);
  const scale = Math.min(1, maxWidth / Math.max(1, img.naturalWidth));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvasToBlob(canvas, "image/jpeg", quality);
}

export async function imagesToPdfBlob(items, { format = "jpg", quality = 0.92 } = {}) {
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let first = true;
  for (const item of items) {
    const src = item.dataUrl || (await blobToDataURL(item.blob));
    const img = await loadImageElement(src);
    const ratio = Math.min(pageWidth / img.naturalWidth, pageHeight / img.naturalHeight);
    const w = img.naturalWidth * ratio;
    const h = img.naturalHeight * ratio;
    if (!first) pdf.addPage();
    pdf.addImage(src, format === "png" ? "PNG" : "JPEG", (pageWidth - w) / 2, (pageHeight - h) / 2, w, h);
    first = false;
  }
  return pdf.output("blob");
}

export function defaultEdits() {
  return { rotation: 0, flipH: false, flipV: false, brightness: 100, contrast: 100, saturate: 100 };
}

export function isEdited(edits) {
  return (
    edits.rotation !== 0 ||
    edits.flipH ||
    edits.flipV ||
    edits.brightness !== 100 ||
    edits.contrast !== 100 ||
    edits.saturate !== 100
  );
}

export function clampEditValue(edits, key, value, min, max) {
  return { ...edits, [key]: clamp(Number(value) || 0, min, max) };
}
